import { useCallback, useMemo } from 'react';
import {
  type AllFees,
  type Amount,
  type CostsAndFees,
  type CostTotal,
  type CostTotals,
  CUSTOM_FEE_TYPE,
  type Fee,
  type FeeOverrides,
  type FlattenedFee,
  type SelectedFeeOption,
  type SelectedFeeOptions,
  type Selector,
} from 'app/types/costAndFees.type';
import { addAmounts, combineFees, expandFees, flattenFees, getSelectedOptionFrom, multiplyAmount } from '../utils';

// Define the extended CostTotals type used internally for calculations
type CalculatedCostTotals = CostTotals & { oneTime: CostTotal };

// --- Helper Functions for Cost Calculation ---

// Combines costs from multiple selectors
const combineMultiSelectorCosts = (allSelectorCosts: Array<CostTotals>): CostTotals => {
  if (!allSelectorCosts || allSelectorCosts.length === 0) {
    // Should not happen if called correctly, but return a default structure
    return {
      moveIn: { displayName: 'Estimated total', amount: { min: 0, max: 0 } },
      monthly: { displayName: 'Estimated monthly total', amount: { min: 0, max: 0 } },
      baseRent: { displayName: 'Base rent', amount: { min: 0, max: 0 } },
    };
  }

  // Deep clone the structure of the first selector's costs
  const combined = JSON.parse(JSON.stringify(allSelectorCosts[0])) as CostTotals;

  // Initialize numeric amounts for aggregation
  Object.keys(combined).forEach((key) => {
    const costKey = key as keyof CostTotals;
    // Add this check:
    if (combined[costKey]) {
      // Set min to Infinity, max to -Infinity to find the true range across all selectors
      combined[costKey].amount = { min: Infinity, max: -Infinity };
    }
  });

  // Aggregate min/max from all selectors
  allSelectorCosts.forEach((selectorCosts) => {
    Object.keys(selectorCosts).forEach((key) => {
      const costKey = key as keyof CostTotals;
      const selectorCost = selectorCosts[costKey];
      const combinedCost = combined[costKey];

      if (!selectorCost || !combinedCost || !('min' in combinedCost.amount)) {
        // Skip if cost type missing or combined structure doesn't have min/max
        return;
      }

      // If any selector has displayText, the combined result becomes displayText for that category
      if ('displayText' in selectorCost.amount) {
        combinedCost.amount = { displayText: selectorCost.amount.displayText };
        return; // Stop processing numeric for this category
      }

      // Aggregate numeric values, only if the combined amount is still numeric
      if ('min' in combinedCost.amount && 'min' in selectorCost.amount) {
        combinedCost.amount.min = Math.min(
          combinedCost.amount.min,
          selectorCost.amount.min ?? Infinity, // Handle potential null min
        );

        // Handle max aggregation carefully with nulls
        const currentMax = combinedCost.amount.max;
        const selectorMax = selectorCost.amount.max; // This is number | null

        if (selectorMax === null) {
          // If any selector has null max, combined max becomes null
          (combinedCost.amount as { min: number; max: number | null }).max = null;
        } else if (currentMax !== null) {
          // If currentMax is numeric and selectorMax is numeric, take the max
          combinedCost.amount.max = Math.max(currentMax, selectorMax);
        }
        // else currentMax is already null, leave it as null
      }
    });
  });

  // Clean up Infinity/-Infinity if no numeric values were found or if only displayText was encountered
  Object.keys(combined).forEach((key) => {
    const costKey = key as keyof CostTotals;
    if (combined[costKey] && 'min' in combined[costKey].amount) {
      const amount = combined[costKey].amount as { min: number; max: number | null };
      if (amount.min === Infinity) {
        // No valid min found, default to 0
        amount.min = 0;
      }
      if (amount.max === -Infinity) {
        // No valid max found, default to min (which might be 0)
        amount.max = amount.min;
      }
    }
  });

  return combined;
};

// Determines the starting costs and fee overrides based on selector overrides
const determineSourceData = (
  baseCosts: CostTotals,
  overrides: Array<Selector>,
): { sourceCosts: CostTotals; sourceFeeOverrides: FeeOverrides | null } => {
  if (overrides.length === 0) {
    return { sourceCosts: baseCosts, sourceFeeOverrides: null };
  }
  if (overrides.length === 1) {
    return {
      sourceCosts: overrides[0].costs,
      sourceFeeOverrides: overrides[0].feeOverrides,
    };
  }
  // Combine costs and overrides from multiple selectors
  const combinedSourceCosts = combineMultiSelectorCosts(overrides.map((s) => s.costs));
  const combinedSourceFeeOverrides = combineFees(overrides.map((s) => s.feeOverrides));
  return { sourceCosts: combinedSourceCosts, sourceFeeOverrides: combinedSourceFeeOverrides };
};

// Initializes the totalCosts structure, including the dynamic 'oneTime' category
const initializeTotalCosts = (sourceCosts: CostTotals): CalculatedCostTotals => {
  // Helper to safely get amount or default numeric (no longer used but kept for potential future use)
  const getInitialAmount = (cost: CostTotal | undefined): Amount => {
    if (cost && 'displayText' in cost.amount) {
      return { displayText: cost.amount.displayText };
    }
    // Default to 0 if cost is missing or numeric
    // Ensure we handle the case where Amount is {min, max: null}
    const numericAmount = cost?.amount && 'min' in cost.amount ? cost.amount : null;
    return {
      min: numericAmount?.min ?? 0,
      max: numericAmount?.max ?? numericAmount?.min ?? 0, // Default max to min if null
    };
  };

  // Deep clone baseRent to avoid modifying the source
  const baseRentClone = sourceCosts.baseRent
    ? JSON.parse(JSON.stringify(sourceCosts.baseRent))
    : { displayName: 'Base rent', amount: { min: 0, max: 0 } };

  return {
    moveIn: {
      // Initialize moveIn amount to 0 (fees will be added separately)
      displayName: sourceCosts.moveIn?.displayName ?? 'Estimated total',
      amount: { min: 0, max: 0 },
    },
    monthly: {
      // ALWAYS initialize monthly amount to 0
      displayName: sourceCosts.monthly?.displayName ?? 'Estimated monthly total',
      amount: { min: 0, max: 0 },
    },
    baseRent: baseRentClone, // Keep initializing baseRent from source
    oneTime: { displayName: 'One-time fees', amount: { min: 0, max: 0 } }, // Always start oneTime numeric 0
  };
};

// Applies base rent to the monthly total
const applyBaseRent = (totalCosts: CalculatedCostTotals): void => {
  const baseRentAmount = totalCosts.baseRent?.amount;
  const monthlyAmount = totalCosts.monthly.amount;

  if (!baseRentAmount) return; // No base rent to apply

  if ('displayText' in baseRentAmount) {
    // If baseRent is "Contact", monthly should become "Contact" unless it already is
    if ('min' in monthlyAmount) {
      totalCosts.monthly.amount = { displayText: baseRentAmount.displayText };
    }
  } else if ('min' in monthlyAmount) {
    // Both are numeric, add baseRent to monthly
    totalCosts.monthly.amount = addAmounts(monthlyAmount, baseRentAmount);
  } else {
    // Monthly is displayText, baseRent is numeric. Replace monthly displayText with baseRent numeric value.
    totalCosts.monthly.amount = JSON.parse(JSON.stringify(baseRentAmount));
  }
};

// Applies a single fee's calculated amount to the correct totals category
const applyFeeToTotals = (
  totalCosts: CalculatedCostTotals,
  fee: FlattenedFee,
  feeOptions: SelectedFeeOption | undefined,
): void => {
  const isOptional = fee.feeGroupType === 'OPTIONAL';

  // For optional fees, skip if not selected or zero count
  if (isOptional && (!feeOptions || feeOptions.counterValue <= 0)) {
    return;
  }

  // For required fees, use the selected count or fallback to minimum required
  const multiplier = feeOptions?.counterValue ?? fee.counter?.min ?? 0;

  // Skip if the effective multiplier is zero
  if (multiplier <= 0) {
    return;
  }
  let feeAmountToApply: Amount = fee.amount;

  // Check if a specific option within the fee was selected
  if (feeOptions?.optionSelected && fee.options && fee.options.length > 0) {
    const optionData = fee.options.find((opt) => opt.displayName === feeOptions.optionSelected);
    if (optionData?.amount) {
      feeAmountToApply = optionData.amount;
    }
  }

  // Calculate the fee's total contribution (amount * multiplier)
  const calculatedFeeAmount = multiplyAmount(feeAmountToApply, multiplier);

  // If the calculated amount ended up as non-numeric (e.g., from "Contact"), skip it
  if ('displayText' in calculatedFeeAmount) {
    // Don't add "Contact" fees to numeric totals - just skip them
    return;
  }

  // Add numeric calculated fee amount to the correct category
  const frequencyKey = fee.frequency as keyof CalculatedCostTotals;
  if (frequencyKey === 'moveIn' || frequencyKey === 'monthly') {
    const totalCategory = totalCosts[frequencyKey];
    if (totalCategory) {
      // If the total was previously displayText, replace it with the first numeric fee
      if ('displayText' in totalCategory.amount) {
        totalCategory.amount = calculatedFeeAmount;
      } else {
        // Otherwise, add numerically
        totalCategory.amount = addAmounts(totalCategory.amount, calculatedFeeAmount);
      }
    }
  }
};

// Performs final adjustments, like restoring "Contact" text or ensuring max >= min
const performFinalAdjustments = (
  totalCosts: CalculatedCostTotals,
  sourceCosts: CostTotals, // The costs before fees were applied
): void => {
  // Restore "Contact" for moveIn if it started as "Contact" and ended as numeric 0
  if (
    sourceCosts.moveIn &&
    'displayText' in sourceCosts.moveIn.amount &&
    'min' in totalCosts.moveIn.amount &&
    totalCosts.moveIn.amount.min === 0 &&
    totalCosts.moveIn.amount.max === 0
  ) {
    totalCosts.moveIn.amount = { displayText: sourceCosts.moveIn.amount.displayText };
  }

  // Restore "Contact" for monthly if it started as "Contact", ended as numeric 0,
  // AND baseRent is also 0 or "Contact".
  const baseRentIsZeroOrContact =
    !totalCosts.baseRent || 'displayText' in totalCosts.baseRent.amount || totalCosts.baseRent.amount.min === 0;

  if (
    sourceCosts.monthly &&
    'displayText' in sourceCosts.monthly.amount &&
    'min' in totalCosts.monthly.amount &&
    totalCosts.monthly.amount.min === 0 &&
    totalCosts.monthly.amount.max === 0 &&
    baseRentIsZeroOrContact
  ) {
    totalCosts.monthly.amount = { displayText: sourceCosts.monthly.amount.displayText };
  }

  // Ensure max >= min for all final amounts
  Object.values(totalCosts).forEach((costCategory) => {
    if (costCategory && 'min' in costCategory.amount) {
      const numericAmount = costCategory.amount; // Should be numeric {min, max} at this point
      if (typeof numericAmount.max === 'number' && numericAmount.max < numericAmount.min) {
        numericAmount.max = numericAmount.min;
      }
    }
  });
};

// Interface for hook params
interface UseCostCalculationParams {
  costsAndFees?: CostsAndFees;
  selectorOverrides: Array<Selector>;
  SelectedFeeOptions: SelectedFeeOptions;
}

/**
 * Hook to manage cost calculations for fees and rent
 */
export const useCostCalculation = ({
  costsAndFees,
  selectorOverrides,
  SelectedFeeOptions,
}: UseCostCalculationParams): CostsAndFees | null => {
  // Memoized function to get the selected option for a fee
  const getSelectedFeeOption = useCallback(
    (fee: Fee) => getSelectedOptionFrom(SelectedFeeOptions)(fee),
    [SelectedFeeOptions],
  );

  // Main calculation function, wrapped in useCallback
  const getCalculatedCostsAndFees = useCallback((): CostsAndFees | null => {
    // If no base data, return null
    if (!costsAndFees) return null;

    // If no selections or overrides, return the original data (cloned)
    if (Object.keys(SelectedFeeOptions).length === 0 && selectorOverrides.length === 0) {
      return costsAndFees ? { ...costsAndFees } : null;
    }

    // 1. Determine Source Costs and Fee Overrides
    const { sourceCosts, sourceFeeOverrides } = determineSourceData(costsAndFees.costs, selectorOverrides);

    // 2. Flatten base fees and apply overrides (MERGE logic instead of replacement)
    let effectiveFees = flattenFees(costsAndFees.fees);

    if (sourceFeeOverrides) {
      const flattenedOverrides = flattenFees(sourceFeeOverrides);

      // Start with base fees and merge in overrides (instead of replacing all)
      // Use displayName in key to avoid collisions between fees of same type (e.g., CUSTOM fees like Water/Sewer)
      const feeMap = new Map(
        effectiveFees.map((f) => [`${f.type}-${f.frequency}-${f.feeGroupType}-${f.displayName}`, f]),
      );

      flattenedOverrides.forEach((overrideFee) => {
        const key = `${overrideFee.type}-${overrideFee.frequency}-${overrideFee.feeGroupType}-${overrideFee.displayName}`;
        // Always apply overrides (custom fees or existing fee updates)
        feeMap.set(key, overrideFee);
      });

      effectiveFees = Array.from(feeMap.values());
    }

    // 3. Initialize Total Costs structure
    const totalCosts = initializeTotalCosts(sourceCosts);

    // 4. Apply Base Rent
    applyBaseRent(totalCosts);

    // 5. Apply selected fees
    const processedFees = new Set<string>(); // Avoid double counting if flattened structure has duplicates somehow
    effectiveFees.forEach((fee) => {
      const feeKey = `${fee.displayName}-${String(fee.frequency)}`; // Use display name as key? Fee type might be better
      if (processedFees.has(feeKey)) return;

      if (fee.type !== 'BASE_RENT') {
        // Base rent already handled
        const feeOptions = getSelectedFeeOption(fee);
        applyFeeToTotals(totalCosts, fee, feeOptions);
        processedFees.add(feeKey);
      }
    });

    // 6. Perform Final Adjustments
    performFinalAdjustments(totalCosts, sourceCosts);

    // 7. Expand fees back to original structure
    const finalFees = expandFees(effectiveFees); // finalFees is of type FeeOverrides

    // 8. Ensure finalFees conforms to AllFees by providing default empty sections
    //    (This step IS necessary because CostsAndFees type requires AllFees, not FeeOverrides)
    const finalFeesResult: AllFees = {
      moveIn: finalFees.moveIn ?? { displayName: 'One-time fees', feeGroup: [] },
      monthly: finalFees.monthly ?? { displayName: 'Monthly rent & fees', feeGroup: [] },
      whenUsed: finalFees.whenUsed ?? { displayName: 'Additional fees', feeGroup: [] },
    };

    // 9. Return the complete calculated structure
    return {
      // Spread original costsAndFees properties like disclaimer, selectors
      // Make sure costsAndFees is defined before spreading
      ...(costsAndFees ?? {}), // Use empty object fallback if costsAndFees is null/undefined
      costs: totalCosts, // The calculated totals
      fees: finalFeesResult, // Use the guaranteed AllFees structure
    };
  }, [costsAndFees, SelectedFeeOptions, selectorOverrides, getSelectedFeeOption]); // Dependencies

  // Calculate costs synchronously and memoize based on dependencies
  const calculatedResult = useMemo(() => getCalculatedCostsAndFees(), [getCalculatedCostsAndFees]);

  return calculatedResult;
};
